
import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Loader2, Package } from "lucide-react";
import { useParcelTypes, ParcelType } from "@/hooks/useParcelTypes";
import ParcelTypesHeader from "./parcel-types/ParcelTypesHeader";
import ParcelTypeForm from "./parcel-types/ParcelTypeForm";
import ParcelTypesList from "./parcel-types/ParcelTypesList";
import OrganizationSelector from "./shared/OrganizationSelector";

const ParcelTypesManager = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedOrgId = searchParams.get('org') || null;
  
  const { parcelTypes, loading, createParcelType, updateParcelType, batchUpdateSortOrder, deleteParcelType } = useParcelTypes(
    true, 
    true, 
    false, 
    selectedOrgId
  );
  
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingType, setEditingType] = useState<ParcelType | null>(null);
  const [reordering, setReordering] = useState(false);

  const handleOrganizationChange = (orgId: string | null) => {
    const newParams = new URLSearchParams(searchParams);
    if (orgId === null) {
      newParams.delete('org');
    } else {
      newParams.set('org', orgId);
    }
    setSearchParams(newParams);
  };

  const handleAddClick = () => {
    setEditingType(null);
    setDialogOpen(true);
  };

  const handleEdit = (parcelType: ParcelType) => {
    setEditingType(parcelType);
    setDialogOpen(true);
  };

  const handleFormSubmit = async (formData: any) => {
    try {
      if (editingType) {
        await updateParcelType(editingType.id, formData);
      } else {
        await createParcelType(formData);
      }
      setDialogOpen(false);
      setEditingType(null);
    } catch (error) {
      console.error('Error submitting parcel type form:', error);
      // Error handling is already in the hook functions
    }
  };

  const handleDelete = async (id: string) => {
    if (confirm('Are you sure you want to delete this parcel type?')) {
      try {
        await deleteParcelType(id);
      } catch (error) {
        console.error('Error deleting parcel type:', error);
        // Error handling is already in the hook function
      }
    }
  };

  const handleToggleEnabled = async (parcelType: ParcelType) => {
    try {
      await updateParcelType(parcelType.id, { is_enabled: !parcelType.is_enabled });
    } catch (error) {
      console.error('Error toggling parcel type:', error);
      // Error handling is already in the hook function
    }
  };

  const handleDragEnd = async (result: any) => {
    if (!result.destination || reordering) return;

    setReordering(true);
    
    try {
      const items = Array.from(parcelTypes);
      const [reorderedItem] = items.splice(result.source.index, 1);
      items.splice(result.destination.index, 0, reorderedItem);

      const batchUpdates = items.map((item, index) => ({
        id: item.id,
        sort_order: index + 1
      }));

      await batchUpdateSortOrder(batchUpdates);
    } catch (error) {
      console.error('Error reordering parcel types:', error);
      // Error handling is already in the hook function
    } finally {
      setReordering(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center p-8">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  const getCurrentContext = () => {
    if (selectedOrgId === null) {
      return "Platform Default";
    }
    return "Organization Specific";
  };

  const getEmptyStateMessage = () => {
    if (selectedOrgId === null) {
      return "No platform default parcel types found. Create one to get started.";
    }
    return "No parcel types found for this organization. Create one or copy platform defaults.";
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader className="border-b bg-muted/50">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-2">
              <Package className="h-5 w-5 text-primary" />
              <div>
                <h2 className="text-lg font-semibold">Parcel Types Management</h2>
                <p className="text-sm text-muted-foreground">
                  Managing {getCurrentContext()} parcel types
                </p>
              </div>
            </div>
            <div className="w-full sm:w-[300px]">
              <OrganizationSelector
                value={selectedOrgId}
                onValueChange={handleOrganizationChange}
                placeholder="Select organization..."
                includeDefault={true}
                defaultLabel="Platform Default"
              />
            </div>
          </div>
        </CardHeader>
        <ParcelTypesHeader onAddClick={handleAddClick} />
        <CardContent>
          {parcelTypes.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <Package className="h-12 w-12 text-muted-foreground mb-4" />
              <h3 className="text-lg font-medium mb-2">No parcel types found</h3>
              <p className="text-muted-foreground mb-4 max-w-md">
                {getEmptyStateMessage()}
              </p>
            </div>
          ) : (
            <ParcelTypesList
              parcelTypes={parcelTypes}
              onDragEnd={handleDragEnd}
              onEdit={handleEdit}
              onDelete={handleDelete}
              onToggleEnabled={handleToggleEnabled}
              reordering={reordering}
            />
          )}
        </CardContent>
      </Card>

      <ParcelTypeForm
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        editingType={editingType}
        onSubmit={handleFormSubmit}
        parcelTypesCount={parcelTypes.length}
      />
    </div>
  );
};

export default ParcelTypesManager;
